import React from 'react';
import { Link } from 'react-router-dom';
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Globe, Lightbulb, CheckCircle } from 'lucide-react';

const solutions = [
  {
    title: 'Digital Transformation',
    description: 'End-to-end digital transformation services to modernize your business operations and customer experiences.',
    features: ['Business Process Automation', 'Legacy System Modernization', 'Cloud Migration', 'Digital Strategy Consulting']
  },
  {
    title: 'Growth Marketing',
    description: 'Data-driven marketing strategies designed to accelerate your business growth and maximize ROI.',
    features: ['SEO & Content Marketing', 'Paid Advertising', 'Conversion Optimization', 'Marketing Analytics']
  },
  {
    title: 'Web & App Development',
    description: 'Custom web and mobile application development tailored to your unique business needs.',
    features: ['Custom Web Applications', 'E-Commerce Platforms', 'Mobile App Development', 'API Integrations']
  },
  {
    title: 'Brand & Design',
    description: 'Strategic branding and design services that help you stand out and connect with your audience.',
    features: ['Brand Identity', 'UI/UX Design', 'Graphic Design', 'Design Systems']
  }
];

export default function SolutionsPage() {
  return (
    <Layout>
   <SEO
  title="Get Complete Digital Solutions for Your Business | Square Trigger"
  description="Find comprehensive digital solutions to transform your business in today's fast-moving market. Request a free quote."
  keywords={SEO_KEYWORDS.solutions}
/>
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="secondary" className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <Globe className="h-4 w-4 mr-2" />
                Our Solutions
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-6 leading-tight"
            >
              Comprehensive{' '}
              <span className="gradient-text">Digital Solutions</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-background/70 mb-8 max-w-2xl mx-auto"
            >
              Integrated digital services designed to help your business thrive in the modern marketplace.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Button variant="gradient" size="lg" asChild className="group">
                <Link to="/get-started">
                  Get Started
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Solutions for Every{' '}
              <span className="gradient-text">Business Need</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover flex flex-col items-center text-center md:items-start md:text-left">
                  <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center mb-6 mx-auto md:mx-0">
                    <Lightbulb className="h-7 w-7 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-foreground mb-4">
                    {solution.title}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {solution.description}
                  </p>
                  <ul className="inline-grid md:grid space-y-3 text-left">
                    {solution.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 w-fit">
                        <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8">
              Let's discuss which solution is right for your business and create a customized plan for success.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
             <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90 min-w-[240px] justify-center">
                <Link to="/get-started">
                  Get Started
                  {/* <ArrowRight className="h-5 w-5 ml-2" /> */}
                </Link>
              </Button>
             <Button variant="glass" size="lg" asChild className="group bg-background text-foreground hover:bg-background/90 min-w-[240px] justify-center">
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
